/*
================================================================================
FILE HEADER — apps/studio-web/src/components/StepList.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed           |
|----|------------|-------------------|--------------------------|------------------------|
| 1  | 2026-06-23 | Claude Sonnet 4.6 | Phase 0 bootstrap        | Created step list UI   |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | Drag-to-reorder (feat 1) | HTML5 drag handles     |
| 3  | 2026-06-25 | Claude Sonnet 4.6 | Step Locator Editor      | ▶ expand button +      |
|    |            |                   | (Option D)               | StepEditor inline;     |
|    |            |                   |                          | onEditCandidates prop  |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/RecorderPanel.tsx

EXPORTS COVERED
---------------
- StepList — renders the recorded steps list with delete / reorder / expand / StepEditor

IMPORTANT POINTS
----------------
1. Each step row has a ▶/▼ toggle that opens StepEditor inline below the row.
2. StepEditor handles both label editing and candidate list editing.
3. onEditStep(index, patch) handles label-only patches from quick edits.
4. onEditCandidates(index, candidates) replaces the full candidate list.
5. Drag-to-reorder: drag handle (⠿), HTML5 drag API, blue top-border on target.
6. The ✏ icon is removed — label editing is part of the expanded StepEditor.
================================================================================
*/

import { useState } from 'react';
import type { IRStep, Candidate } from '@plex/ir';
import { stepLabel } from '../ir/builder.js';
import { StepEditor } from './StepEditor.js';

type Props = {
  steps: IRStep[];
  replayingIndex: number | null;
  stepStatuses?: Record<string, 'ok' | 'not_found' | 'error'>;
  onDelete:          (index: number) => void;
  onEdit?:           (index: number, patch: Record<string, unknown>) => void;
  onEditCandidates?: (index: number, candidates: Candidate[]) => void;
  onReorder?:        (from: number, to: number) => void;
  onReplay:          () => void;
  compact?:          boolean;
};

const STRATEGY_COLOR: Record<string, string> = {
  a11yId: '#22c55e', text: '#22c55e', contentDesc: '#22c55e', className: '#22c55e',
  relative: '#eab308', textContains: '#eab308', textRegex: '#eab308',
  css: '#ef4444', xpath: '#ef4444',
};

function primaryStrategy(step: IRStep): string | null {
  if (!('selector' in step) || !step.selector) return null;
  const first = (step.selector as { candidates: Candidate[] }).candidates[0];
  return first?.strategy ?? null;
}

const STEP_TYPE_ICON: Record<string, string> = {
  openUrl: '🌐', deepLink: '🔗', tap: '👆', doubleTap: '👆', longPress: '✋',
  typeText: '⌨️', clearText: '🗑️', wait: '⏱️', waitFor: '⏳', waitForNetwork: '📡',
  assert: '✅', screenshot: '📷', print: '📝', script: '⚙️', api: '🌐',
  if: '🔀', repeat: '🔁', callFlow: '📦', setVariable: '📦', getAttribute: '🏷️',
  getCount: '🔢', evaluate: '=', checkpoint: '🏁', networkAssert: '📡', visualAssert: '👁️',
  scrollUntilVisible: '🖱️',
};

const STATUS_ICON:  Record<string, string> = { ok: '✅', not_found: '❌', error: '⚠️' };
const STATUS_COLOR: Record<string, string> = { ok: '#22c55e', not_found: '#ef4444', error: '#f59e0b' };

export function StepList({
  steps, replayingIndex, stepStatuses = {},
  onDelete, onEdit, onEditCandidates, onReorder, onReplay, compact = false,
}: Props) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [dragIdx,     setDragIdx]     = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  if (steps.length === 0) {
    if (compact) return null;
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center', color: '#52525b', fontSize: 13 }}>
        No steps yet. Connect a browser tab and click Record to start.
      </div>
    );
  }

  const handleDragStart = (e: React.DragEvent, i: number) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver  = (e: React.DragEvent, i: number) => { e.preventDefault(); if (dragOverIdx !== i) setDragOverIdx(i); };
  const handleDrop      = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null); setDragOverIdx(null);
    if (from !== null && from !== i && onReorder) onReorder(from, i);
  };
  const handleDragEnd   = () => { setDragIdx(null); setDragOverIdx(null); };

  return (
    <div>
      {!compact && (
        <div style={{ borderBottom: '1px solid #27272a', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#71717a', flex: 1 }}>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
          <button onClick={onReplay} disabled={steps.length === 0} style={btnStyle('#7c3aed')}>▶ Replay</button>
        </div>
      )}

      {steps.map((step, i) => {
        const isReplaying  = replayingIndex === i;
        const isExpanded   = expandedIdx === i;
        const isDragging   = dragIdx === i;
        const isDragOver   = dragOverIdx === i && dragIdx !== i;
        const strategy     = primaryStrategy(step);
        const colour       = strategy ? (STRATEGY_COLOR[strategy] ?? '#71717a') : '#71717a';
        const icon         = STEP_TYPE_ICON[step.type] ?? '•';
        const hasSelector  = 'selector' in step && step.selector;

        return (
          <div
            key={step.id}
            draggable={!!onReorder}
            onDragStart={(e) => handleDragStart(e, i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            onDragEnd={handleDragEnd}
            style={{
              borderBottom: '1px solid #1c1c1f',
              borderTop: isDragOver ? '2px solid #7c3aed' : undefined,
              opacity: isDragging ? 0.4 : 1,
            }}
          >
            {/* ── Step row ── */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 12px',
              background: isReplaying ? '#1c1040'
                : stepStatuses[step.id] === 'ok'        ? 'rgba(34,197,94,0.07)'
                : stepStatuses[step.id] === 'not_found' ? 'rgba(239,68,68,0.07)'
                : stepStatuses[step.id] === 'error'     ? 'rgba(245,158,11,0.07)'
                : isExpanded                             ? '#111113'
                : 'transparent',
            }}>
              {/* Drag handle */}
              {onReorder && (
                <span style={{ color: '#3f3f46', cursor: 'grab', fontSize: 12, flexShrink: 0, userSelect: 'none' }}>⠿</span>
              )}
              {/* Step number / status */}
              <span style={{ color: stepStatuses[step.id] ? STATUS_COLOR[stepStatuses[step.id]!] : '#52525b', fontSize: 12, minWidth: 18, textAlign: 'right' }}>
                {stepStatuses[step.id] ? STATUS_ICON[stepStatuses[step.id]!] : i + 1}
              </span>
              {/* Icon */}
              <span style={{ fontSize: 13 }}>{icon}</span>
              {/* Label */}
              <span style={{ flex: 1, fontSize: 12, color: '#d4d4d8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {stepLabel(step)}
              </span>
              {/* Strategy badge */}
              {strategy && (
                <span style={{ fontSize: 9, color: colour, border: `1px solid ${colour}`, borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>
                  {strategy}
                </span>
              )}
              {/* Expand toggle — opens StepEditor */}
              <button
                onClick={() => setExpandedIdx(isExpanded ? null : i)}
                title={isExpanded ? 'Close editor' : 'Edit label & selector candidates'}
                style={{
                  background: isExpanded ? '#27272a' : 'none', border: 'none',
                  color: isExpanded ? '#c4b5fd' : '#52525b', cursor: 'pointer',
                  padding: '2px 5px', fontSize: 11, borderRadius: 3, flexShrink: 0,
                }}
                onMouseEnter={e => { if (!isExpanded) (e.currentTarget).style.color = '#a78bfa'; }}
                onMouseLeave={e => { if (!isExpanded) (e.currentTarget).style.color = '#52525b'; }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
              {/* Delete */}
              <button
                onClick={() => { onDelete(i); if (expandedIdx === i) setExpandedIdx(null); }}
                title="Delete step"
                style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '2px 4px', fontSize: 14, lineHeight: 1, flexShrink: 0 }}
                onMouseEnter={e => { (e.currentTarget).style.color = '#ef4444'; }}
                onMouseLeave={e => { (e.currentTarget).style.color = '#52525b'; }}
              >×</button>
            </div>

            {/* ── Inline StepEditor ── */}
            {isExpanded && (
              <StepEditor
                step={step}
                onSave={(label, candidates) => {
                  onEdit?.(i, { label });
                  if (hasSelector) onEditCandidates?.(i, candidates);
                  setExpandedIdx(null);
                }}
                onClose={() => setExpandedIdx(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function btnStyle(bg: string): React.CSSProperties {
  return { background: bg, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 500 };
}
